import { AppContext } from 'App.context';
import { useEffect } from 'react';
import { useContext, useState } from 'react';
import { ApiContext } from 'utils/networking/Api.context';
import { capitalize, camelToUnderscore } from 'utils/TextUtils';
import storage from '../../../utils/storage';
import { notification } from 'antd';

export default function useMarketingHook() {
  const { appState, appActions } = useContext(AppContext);
  const { API } = useContext(ApiContext);
  const chainSlug = appActions.getSelectedChain()?.slug;
  const { translate, getSelectedHotel } = appActions;

  const [state, setState] = useState({
    hotel: null,
    data: null,
    configOptions: null,
    showChangeSlugModal: false,
    showChangeSlugModalLoading: false,
    hotelSlugToBeChanged: getSelectedHotel()?.slug,
    chainSlugToBeChanged: chainSlug,
    slugTypeToBeChanged: null,
    changeChainSlugErrorMessage: null,
    selectedEntranceDateFormat: null,
  });

  useEffect(() => {
    const selectedHotel = getSelectedHotel();

    actions.setHotel(selectedHotel);

    updateState({
      hotelSlugToBeChanged: selectedHotel?.slug,
      chainSlugToBeChanged: actions.getChainSlug(),
    });
    actions.getHotelCheckinConfig(selectedHotel);
  }, [appState.selectedHotel]);

  const actions = {
    loadTableLinks: () => {
      const data = appState?.hotelList?.map((hotel, index) => {
        return {
          key: index + 1,
          name: capitalize(hotel?.name),
          url: `https://checkin.civitfun.com/hotel/${hotel?.slug}`,
        };
      });

      if (data && data.length) {
        data.unshift({
          key: 0,
          name: translate('marketing.chainCheckin'),
          url: `https://checkin.civitfun.com/chain/${actions.getChainSlug()}`,
        });
      }

      updateState({ data });
    },

    setHotel: hotel => {
      updateState({ hotel });
      actions.loadTableLinks();
      actions.loadCheckinConfig(hotel?.key);
    },

    getSelectedHotelSlug: () => {
      const completeHotel = appState?.hotelList?.find(
        savedHotel => savedHotel.id == state.hotel?.key
      );
      return completeHotel?.slug;
    },

    getChainId: () => {
      return appActions.getSelectedChain()?.id || null;
    },

    getChainSlug: () => {
      return appActions.getSelectedChain()?.slug || null;
    },

    loadCheckinConfig: hotelId => {
      if (!hotelId) return;
      updateState({ isLoading: true });
      API.bookingSearchConfigRequest(hotelId)
        .then(res => {
          updateState({
            isLoading: false,
            configOptions: res,
          });
        })
        .catch(_ => {});
    },

    getBookingIdentifierLink: () => {
      return `https://checkin.civitfun.com/hotel/${actions.getSelectedHotelSlug()}/startCheckin/[BOOKING_IDENTIFIER]`;
    },

    getHotelDeeplink: () => {
      let link = `https://checkin.civitfun.com/hotel/${actions.getSelectedHotelSlug()}/bookingSearchForm?`;

      if (state.configOptions && state.configOptions.length) {
        const filteredOptions = state.configOptions.filter(option => option.state === 'active');

        filteredOptions.forEach((option, index) => {
          link = `${link}${option.name}=[${camelToUnderscore(option.name)}]`;

          if (index < filteredOptions.length - 1) {
            link = `${link}&`;
          }
        });
      }

      link = `${link}&autoSearch`;

      return link;
    },

    getHotelDeeplinkBooking: () => {
      let link = `https://checkin.civitfun.com/hotel/${actions.getSelectedHotelSlug()}/bookingSearchForm?channel=bookingcommessages`;

      return link;
    },

    redirectToDeeplinkBooking: () => {
      const link = actions.getHotelDeeplink();
      window.open(link, '_blank');
    },

    toggleChangeSlugModal: () => {
      updateState({
        showChangeSlugModal: !state.showChangeSlugModal,
        showChangeSlugModalLoading: false,
      });
    },

    openChangeSlugModal: isChangingHotelSlug => {
      if (isChangingHotelSlug) {
        updateState({
          showChangeSlugModal: true,
          slugTypeToBeChanged: 'hotel',
        });
      } else {
        updateState({
          showChangeSlugModal: true,
          slugTypeToBeChanged: 'chain',
        });
      }
    },

    onHotelSlugChanged: slug => {
      updateState({
        hotelSlugToBeChanged: slug,
      });

      if (validateSlug(slug) === false) {
        updateState({
          changeHotelSlugErrorMessage: translate('marketing.slugValidationError'),
        });
      } else {
        updateState({
          changeHotelSlugErrorMessage: null,
        });
      }
    },

    onChainSlugChanged: slug => {
      updateState({
        chainSlugToBeChanged: slug,
      });

      if (validateSlug(slug) === false) {
        updateState({
          changeChainSlugErrorMessage: translate('marketing.slugValidationError'),
        });
      } else {
        updateState({
          changeChainSlugErrorMessage: null,
        });
      }
    },

    updateSlug: () => {
      updateState({ showChangeSlugModalLoading: true });
      const slugToBeChanged =
        state.slugTypeToBeChanged === 'hotel'
          ? state.hotelSlugToBeChanged
          : state.chainSlugToBeChanged;

      if (state.slugTypeToBeChanged === 'hotel') {
        API.setHotelSlugRequest(getSelectedHotel().id, slugToBeChanged)
          .then(res => {
            appActions.setSelectedHotel({
              ...getSelectedHotel(),
              slug: slugToBeChanged,
            });

            appActions.setHotelList(
              appState.hotelList.map(hotel =>
                hotel.id === getSelectedHotel().id ? { ...hotel, slug: slugToBeChanged } : hotel
              )
            );

            notification.success({
              message: translate('marketing.slugChangeSuccess'),
            });
            updateState({
              changeHotelSlugErrorMessage: null,
            });
          })
          .catch(err => {
            updateState({
              changeHotelSlugErrorMessage:
                err?.response?.data?.status === 409
                  ? translate('marketing.slugAlreadyExistsError')
                  : null,
            });

            if (err?.response?.data?.status !== 409) {
              notification.error({
                message: translate('marketing.slugChangeError'),
              });
            }
          })
          .finally(() => {
            updateState({
              showChangeSlugModalLoading: false,
              showChangeSlugModal: false,
            });
          });
        return;
      }

      if (state.slugTypeToBeChanged === 'chain') {
        API.setChainSlugRequest(actions.getChainId(), slugToBeChanged)
          .then(res => {
            appActions.setChain({
              ...appActions.getSelectedChain(),
              slug: slugToBeChanged,
            });

            notification.success({
              message: translate('marketing.slugChangeSuccess'),
            });
            updateState({
              changeChainSlugErrorMessage: null,
            });
          })
          .catch(err => {
            updateState({
              changeChainSlugErrorMessage:
                err?.response?.data?.status === 409
                  ? translate('marketing.slugAlreadyExistsError')
                  : null,
            });

            if (err?.response?.data?.status !== 409) {
              notification.error({
                message: translate('marketing.slugChangeError'),
              });
            }
          })
          .finally(() => {
            updateState({
              showChangeSlugModalLoading: false,
              showChangeSlugModal: false,
            });
          });
        return;
      }
    },
    setSelectedValue: (field, value) => {
      updateState({ [field]: value });
    },

    getHotelCheckinConfig: selectedHotel => {
      API.getHotelCheckinConfig({ hotelId: selectedHotel.key }).then(res => {
        updateState({ selectedEntranceDateFormat: res.bookingSearchDateFormat });
      });
    },
    updateEntranceDateFormat: () => {
      if (!state.selectedEntranceDateFormat) return;
      const selectedHotel = getSelectedHotel();

      const body = {
        hotelId: selectedHotel.key,
        bookingSearchDateFormat: state.selectedEntranceDateFormat,
      };
      API.updateHotelCheckinConfig({ body })
        .then(res => {
          notification.success({
            message: translate('marketing.changeEntranceDateSuccess'),
          });
        })
        .catch(err => {
          notification.error({
            message: translate('marketing.changeEntranceDateError'),
          });
        });
    },
  };

  function updateState(object) {
    setState(previousState => ({ ...previousState, ...object }));
  }

  function validateSlug(slug) {
    const regex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
    return regex.test(slug);
  }

  return { state, actions };
}
